import { CornerMarks, DotMatrixAccent } from "@/components/primitives";
import { BlurFade } from "@/components/ui/blur-fade";
import { cn } from "@/lib/utils";

/* ── Award data ──────────────────────────────────────────────── */

const AWARDS = [
  {
    number: "01",
    name: "Shorty Awards",
    image: "/images/awards/shorty-awards.avif",
    description:
      "First place for Social Activation in a digital campaign for Shine A Light.",
  },
  {
    number: "02",
    name: "Clutch 1000",
    image: "/images/awards/clutch-1000.avif",
    description:
      "Named a Clutch 1000 Global Leader among B2B firms worldwide.",
  },
  {
    number: "03",
    name: "The Netty Awards",
    image: "/images/awards/netty-awards.svg",
    description:
      "First place Netty Award for Best Digital Campaign.",
  },
  {
    number: "04",
    name: "Google",
    image: "/images/awards/google-partner.avif",
    description:
      "Selected by Google to train its top Premier Partners on campaign automation.",
  },
  {
    number: "05",
    name: "New York Festivals",
    image: "/images/awards/new-york-festivals.avif",
    description:
      "Shortlisted for best digital campaign in sports and fitness for Slinger Bag.",
  },
  {
    number: "06",
    name: "Expertise.com",
    image: "/images/awards/expertise.avif",
    description:
      "Awarded Top 32 Advertising Agencies in NYC.",
  },
];

/* ── Award card ──────────────────────────────────────────────── */

function AwardCard({ award, index }: { award: (typeof AWARDS)[number]; index: number }) {
  return (
    <div
      className="relative"
      style={{ padding: "var(--pad-md)", minHeight: 200 }}
    >
      <CornerMarks dark={false} />
      {index === 0 && <DotMatrixAccent position="top-right" size="sm" />}
      {index === 5 && <DotMatrixAccent position="bottom-left" size="sm" />}

      <div
        style={{
          height: 64,
          marginBottom: "var(--space-gap-sm)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <img
          src={award.image}
          alt={award.name}
          className="object-contain"
          // width/height attrs prevent CLS — browser reserves the aspect
          // ratio before the lazy image decodes. The CSS style overrides
          // for actual display (height 56, width auto, max 70% of container).
          width={140}
          height={56}
          style={{ height: 56, width: "auto", maxWidth: "70%" }}
          loading="lazy"
        />
      </div>

      <h3 className="type-h4" style={{ color: "var(--charcoal)" }}>
        {award.name}
      </h3>

      <p
        className="type-body"
        style={{ color: "var(--text-muted-on-light)", marginTop: "var(--space-gap-xs)" }}
      >
        {award.description}
      </p>
    </div>
  );
}

/* ── Component ───────────────────────────────────────────────── */

export interface AwardsSectionV2Props {
  className?: string;
}

export function AwardsSectionV2({ className }: AwardsSectionV2Props) {
  return (
    <section
      className={cn("relative bg-cream overflow-hidden", className)}
      style={{
        paddingTop: "clamp(40px, 5vw, 80px)",
        paddingBottom: "var(--space-section-bottom)",
      }}
      data-testid="section-awards"
    >
      <div className="container-content relative">
        {/* Header */}
        <BlurFade inView delay={0.1}>
          <span
            className="type-eyebrow"
            style={{ color: "var(--terra)" }}
          >
            RECOGNITION
          </span>
        </BlurFade>

        <BlurFade inView delay={0.15}>
          <div style={{ maxWidth: "var(--max-w-heading)" }}>
            <h2
              className="type-h2"
              style={{
                color: "var(--charcoal)",
                marginTop: "var(--space-gap-sm)",
                marginBottom: "var(--space-gap-lg)",
              }}
            >
              We don't do this for the trophies. But we're not going to pretend they don't look good on the shelf.
            </h2>
          </div>
        </BlurFade>

        {/* Desktop: plain 3-col grid, no shared borders */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-3" style={{ gap: "var(--gap-xs)" }}>
          {AWARDS.map((award, i) => (
            <BlurFade key={award.number} inView delay={0.1 + i * 0.06} className="h-full">
              <div className="h-full" style={{ border: "var(--border-structural)" }}>
                <AwardCard award={award} index={i} />
              </div>
            </BlurFade>
          ))}
        </div>

        {/* Mobile: spaced cards */}
        <div className="md:hidden flex flex-col" style={{ gap: "var(--gap-xs)" }}>
          {AWARDS.map((award, i) => (
            <BlurFade key={award.number} inView delay={0.1 + i * 0.06}>
              <div style={{ border: "var(--border-structural)" }}>
                <AwardCard award={award} index={i} />
              </div>
            </BlurFade>
          ))}
        </div>
      </div>
    </section>
  );
}
